import React, { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Copy, Check, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface PasswordOptions {
    length: number;
    includeUppercase: boolean;
    includeLowercase: boolean;
    includeNumbers: boolean;
    includeSymbols: boolean;
}

interface PasswordGeneratorCardProps {
    initialPassword?: string;
    initialOptions?: PasswordOptions;
    isDarkMode: boolean;
}

const generatePassword = (options: PasswordOptions): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    let charset = '';
    if (options.includeUppercase) charset += uppercase;
    if (options.includeLowercase) charset += lowercase;
    if (options.includeNumbers) charset += numbers;
    if (options.includeSymbols) charset += symbols;

    if (!charset) charset = lowercase + numbers;

    let password = '';
    for (let i = 0; i < options.length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    return password;
};

export const PasswordGeneratorCard: React.FC<PasswordGeneratorCardProps> = ({
    initialPassword = '',
    initialOptions,
    isDarkMode
}) => {
    const { toast } = useToast();
    const [copied, setCopied] = useState(false);

    const defaultOptions: PasswordOptions = {
        length: 12,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: false,
        ...initialOptions
    };

    const [options, setOptions] = useState<PasswordOptions>(defaultOptions);
    const [password, setPassword] = useState<string>(initialPassword || generatePassword(defaultOptions));

    const handleGenerate = useCallback(() => {
        const newPassword = generatePassword(options);
        setPassword(newPassword);
        setCopied(false);
    }, [options]);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(password);
            setCopied(true);
            toast({ title: 'Copied!' });
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast({ title: 'Failed to copy', variant: 'destructive' });
        }
    }, [password, toast]);

    const handleLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newLength = Math.min(Math.max(parseInt(e.target.value) || 4, 4), 30);
        setOptions(prev => ({ ...prev, length: newLength }));
    };

    const handleOptionChange = (key: keyof PasswordOptions) => {
        setOptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Card
            className={`overflow-hidden w-full max-w-md border-[1px] rounded-[16px] transition-colors duration-300 ${
                isDarkMode 
                ? 'bg-[#1a1a1a] border-[#3c3c3c]' 
                : 'bg-white border-gray-400'
            }`}
        >

            {/* Content */}
            <div className="p-5 space-y-6">
                
                {/* Password Display */}
                <div className={`flex items-center justify-between rounded-lg px-4 py-3 border transition-colors ${
                    isDarkMode 
                    ? 'bg-[#252525] border-[#3c3c3c]' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                    <span className={`font-mono text-base tracking-wide truncate flex-1 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                        {password}
                    </span>
                    <button
                        onClick={handleCopy}
                        className={`ml-3 p-2 rounded-md transition-colors ${
                            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'
                        }`}
                        title="Copy password"
                    >
                        {copied ? (
                            <Check className="w-5 h-5 text-green-500" />
                        ) : (
                            <Copy className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        )}
                    </button>
                </div>

                {/* Length Slider */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Password Length
                        </label>
                        <span className={`text-sm font-semibold bg-transparent px-2 py-1 rounded ${
                            isDarkMode ? 'text-white' : 'text-black'
                        }`}>
                            {options.length}
                        </span>
                    </div>
                    <input
                        type="range"
                        min="4"
                        max="30"
                        value={options.length}
                        onChange={handleLengthChange}
                        className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-all ${
                            isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                        }`}
                    />
                </div>

                {/* Generate Button */}
                <button
                    onClick={handleGenerate}
                    className={`w-full py-3 px-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg ${
                        isDarkMode 
                        ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-900/20' 
                        : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                    }`}
                >
                    <RefreshCw className="w-4 h-4" />
                    Generate Password
                </button>

                {/* Options Grid */}
                <div className={`grid grid-cols-2 gap-2 pt-4 border-t ${
                    isDarkMode ? 'border-gray-800' : 'border-gray-100'
                }`}>
                    {(['includeNumbers', 'includeUppercase', 'includeLowercase', 'includeSymbols'] as const).map((key) => (
                        <label
                            key={key}
                            className="flex items-center gap-3 cursor-pointer group select-none"
                        >
                            <div className="relative">
                                <input 
                                    type="checkbox"
                                    checked={options[key]}
                                    onChange={() => handleOptionChange(key)}
                                    className="sr-only"
                                />
                                <div className={`w-6 h-6 rounded-md border-[1px] flex items-center justify-center transition-all ${
                                    options[key] 
                                        ? (isDarkMode ? 'bg-blue-500 border-blue-500' : 'bg-blue-700 border-blue-400')
                                        : (isDarkMode ? 'border-gray-600 bg-transparent' : 'border-gray-300 bg-white')
                                }`}>
                                    {options[key] && <Check className="w-4 h-4 text-white stroke-[1px]" />}
                                </div>
                            </div>
                            <span className={`text-sm font-medium transition-colors ${
                                isDarkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'
                            }`}>
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </span>
                        </label>
                    ))}
                </div>
            </div>
        </Card>
    );
};

export default PasswordGeneratorCard;